import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Event, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { selectAuthLoading } from './store/auth/auth.selectors';
import { LoadingOverlayComponent } from './shared/components/loading-overlay/loading-overlay.component';
import { ConfirmDialogComponent } from './shared/components/confirm-dialog/confirm-dialog.component';
import { ReauthDialogComponent } from './shared/components/reauth-dialog/reauth-dialog.component';
import { ExternalViewerDialogComponent } from './shared/components/external-viewer-dialog/external-viewer-dialog.component';
import { PwaInstallBannerComponent } from './shared/components/pwa-install-banner/pwa-install-banner.component';
import { ComingSoonComponent } from './pages/coming-soon/coming-soon.component';
import { ComingSoonService } from './core/services/coming-soon.service';
import { map } from 'rxjs/operators';
import { combineLatestWith } from 'rxjs';
import { stripAppBase } from './core/config/app-base';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, LoadingOverlayComponent, ConfirmDialogComponent, ReauthDialogComponent, ExternalViewerDialogComponent, PwaInstallBannerComponent, ComingSoonComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  title = 'nibni';
  private store = inject(Store);
  private comingSoonService = inject(ComingSoonService);
  private router = inject(Router);
  loading$ = this.store.select(selectAuthLoading);

  /** Routes that bypass the coming-soon gate (e.g. brochure share links). */
  private readonly BYPASS_PREFIXES = ['/share/file/'];
  private onBypassRoute = signal(
    this.isOnBypassRoute(window.location.pathname + window.location.hash)
  );

  /** Show gate only when coming-soon is enabled AND not on a bypass route. */
  showComingSoon$ = this.comingSoonService.isComingSoon().pipe(
    combineLatestWith(this.router.events),
    map(([comingSoon, event]) => {
      if (event instanceof NavigationEnd) {
        this.onBypassRoute.set(this.isOnBypassRoute(event.urlAfterRedirects));
      }
      return comingSoon && !this.onBypassRoute();
    })
  );

  private isOnBypassRoute(url: string): boolean {
    // Accepts a router URL (`/share/file/xxx`), a browser pathname that still
    // carries the Catalyst client mount (`/app/share/file/xxx`), or a legacy hash
    // URL (`…#/share/file/xxx`).
    const raw = url.includes('#') ? url.split('#')[1] || '' : url;
    const path = stripAppBase(raw);
    return this.BYPASS_PREFIXES.some((prefix) => path.startsWith(prefix));
  }

  constructor() {
    // GA4 SPA page tracking: gtag only exists once the GA snippet in index.html
    // is active (real Measurement ID pasted); before that this is a no-op.
    this.router.events.subscribe((event: Event) => {
      if (!(event instanceof NavigationEnd)) return;
      window.gtag?.('event', 'page_view', {
        page_path: event.urlAfterRedirects,
        page_title: document.title,
      });
    });

    this.router.events.subscribe((event: Event) => {
      if (!(event instanceof NavigationError)) return;
      const msg = (event.error?.message || '').toString();
      const isChunkError =
        event.error?.name === 'ChunkLoadError' ||
        msg.includes('ChunkLoadError') ||
        msg.includes('Loading chunk');
      if (isChunkError) {
        const reloadKey = 'chunk-reload-attempted';
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, '1');
          window.location.reload();
        } else {
          sessionStorage.removeItem(reloadKey);
        }
      }
    });
  }
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}
